import { navigateTo } from '#imports'

import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type { CartItemInput } from '~/interfaces/CartItemInput'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import {
  CartClearQuerySchema,
  CartClearSuccessSchema,
  CartDestroyQuerySchema,
  CartDestroySuccessSchema,
  CartStoreSuccessSchema,
  CartUpdatePathSchema,
  CartUpdateSuccessSchema
} from '~/services/api/generated'
import { CartUpdate } from '~/services/api/contracts/requests/CartUpdate'
import { resolveProblemMessage } from '~/services/api/resolveProblemMessage'
import { FormProblemState } from '~/services/forms/FormProblemState'
import { useCartStore } from '~/stores/useCartStore'
import { buildAddItemPayload } from '~/utils/buildAddItemPayload'
import { holdsCustomerRole } from '~/utils/holdsCustomerRole'
import { identityGeneration } from '~/utils/identityGeneration'
import { resolveRoleNames } from '~/utils/resolveRoleNames'
import { transformCustomizationsToApi } from '~/utils/transformCustomizationsToApi'

interface CartOperation<Result> {
  generation: number
  isCurrent?: () => boolean
  staleResult: Result
  run: () => Promise<Result>
  onFailure: (err: unknown) => Result
}

type CartCommand = Omit<CartOperation<undefined>, 'staleResult'>

/** The cart's requests: every action calls the API, then records the outcome in the cart store. */
export function useCartActions() {
  const cartStore = useCartStore()
  const logger = useLogger('CartStore')
  const { getGuestToken } = useGuestToken()
  const $apiClient = useApiClient()
  const sanctumClient = useApiRequest()
  const { identity, isAuthenticated } = useIdentity()
  const toast = useToast()
  const t = usePresentationTranslator()

  const toCartProblem = (error: unknown, fallback: () => string): FormProblemState => {
    const problem = AppProblemModel.normalize(error)
    return FormProblemState.fromMessage(
      () => resolveProblemMessage(problem, t, fallback()),
      problem.requestId
    )
  }

  const isCustomerRole = () => {
    const userStore = useUserStore()
    const resolvedUser = identity.value ?? userStore.user
    return holdsCustomerRole(resolveRoleNames(resolvedUser))
  }

  const getApiBaseUrl = (): string => {
    const baseUrl = useRuntimeConfig().public.apiBaseUrl
    if (!baseUrl) {
      throw new Error('NUXT_PUBLIC_API_BASE_URL is not set — see .env.example')
    }
    return baseUrl
  }

  const resolveGuestToken = (guestToken: string | null): string => guestToken ?? getGuestToken()

  // CartController reads X-Guest-Token before query/body; every cart mutation sends it.
  const guestTokenHeaders = (token: string | null): Record<string, string> =>
    token !== null && token !== '' ? { 'X-Guest-Token': token } : {}

  const canUseCart = () => !isAuthenticated.value || isCustomerRole()

  const cartRequestContext = (guestToken: string | null) => ({
    baseUrl: getApiBaseUrl(),
    token: resolveGuestToken(guestToken)
  })

  const reportCartFailure = (problem: FormProblemState) => {
    cartStore.setProblem(problem)
    toast.error(problem.messages.join(' '))
  }

  // Tracks the operation as in flight; a failure is recovered only while its identity is current.
  const runCartOperation = async <Result>(operation: CartOperation<Result>): Promise<Result> => {
    const isCurrent =
      operation.isCurrent ?? (() => identityGeneration.isCurrent(operation.generation))
    cartStore.beginOperation(operation.generation)
    cartStore.setProblem(null)
    try {
      return await operation.run()
    } catch (err) {
      return isCurrent() ? operation.onFailure(err) : operation.staleResult
    } finally {
      cartStore.endOperation(operation.generation)
    }
  }

  const runCartCommand = (command: CartCommand): Promise<undefined> =>
    runCartOperation<undefined>({ ...command, staleResult: undefined })

  const recordCartError = (err: unknown, fallback: () => string) => {
    cartStore.setProblem(toCartProblem(err, fallback))
  }

  const fetchCart = async (
    guestToken: string | null = null,
    generation: number = identityGeneration.current()
  ) => {
    if (!canUseCart()) return

    const fetchId = cartStore.claimFetchId()
    const isLatest = () =>
      identityGeneration.isCurrent(generation) && cartStore.isLatestFetch(fetchId)
    await runCartCommand({
      generation,
      isCurrent: isLatest,
      run: async () => {
        const { token } = cartRequestContext(guestToken)
        const response = await $apiClient
          .cart()
          .index(token, { order_type: useSearchStore().orderType })
        if (!isLatest()) return
        if (response.success) cartStore.applyCartIndex(response)
        else
          cartStore.setProblem(
            FormProblemState.fromMessage(() => t('common.cart.fetchFailed'), null)
          )
      },
      onFailure: (err) => {
        recordCartError(err, () => t('common.cart.fetchFailed'))
      }
    })
  }

  // Resolves false when the identity changed during the refresh; the store is selected only on true.
  const refreshCartForStore = async (
    token: string,
    generation: number,
    storeSlug: string
  ): Promise<boolean> => {
    await fetchCart(token, generation)
    if (!identityGeneration.isCurrent(generation)) return false
    cartStore.setCurrentStore(storeSlug)
    return true
  }

  // True only once the server has accepted the POST -- a later fetchCart refresh failure still
  // sets the cart problem but must not turn this accepted add into a reported failure.
  const addItem = async (
    item: CartItemInput,
    storeSlug: string,
    guestToken: string | null = null,
    requestedDate: string | null = null,
    requestedTime: string | null = null
  ): Promise<boolean> => {
    logger.debug('addItem', { item, storeSlug, requestedDate, requestedTime })
    // Captured before the first await: every step below belongs to this identity.
    const generation = identityGeneration.current()
    return runCartOperation<boolean>({
      generation,
      staleResult: false,
      run: async () => {
        const { baseUrl, token } = cartRequestContext(guestToken)
        const apiPayload = buildAddItemPayload(item, storeSlug, requestedDate, requestedTime)
        const response = await sanctumClient(`${baseUrl}/api/cart`, {
          method: 'POST',
          body: apiPayload,
          headers: guestTokenHeaders(token)
        })

        const actualResponse = CartStoreSuccessSchema.parse(response)
        if (!identityGeneration.isCurrent(generation)) return false
        const serverAccepted = typeof actualResponse === 'string' || actualResponse.success
        if (!serverAccepted) {
          reportCartFailure(
            FormProblemState.fromMessage(() => t('common.cart.addItemFailed'), null)
          )
          return false
        }
        if (!(await refreshCartForStore(token, generation, storeSlug))) return false
        cartStore.setStoreCartOpen(true)
        return true
      },
      onFailure: (err) => {
        reportCartFailure(toCartProblem(err, () => t('common.cart.addItemFailed')))
        return false
      }
    })
  }

  const updateItem = async (
    cartItemId: number,
    item: CartItemInput,
    storeSlug: string,
    guestToken: string | null = null
  ) => {
    // Captured before the first await: every step below belongs to this identity.
    const generation = identityGeneration.current()
    await runCartCommand({
      generation,
      run: async () => {
        const { token } = cartRequestContext(guestToken)

        const path = CartUpdatePathSchema.parse({ cartItem: cartItemId })
        const apiPayload = CartUpdate({}).parse({
          quantity: item.quantity ?? 1,
          special_instructions: item.special_instructions ?? '',
          customizations: transformCustomizationsToApi(item.customizations),
          guest_token: token
        })

        CartUpdateSuccessSchema.parse(await $apiClient.cart().update(path.cartItem, apiPayload))
        if (!identityGeneration.isCurrent(generation)) return

        if (await refreshCartForStore(token, generation, storeSlug)) {
          cartStore.setStoreCartOpen(true)
        }
      },
      onFailure: (err) => {
        reportCartFailure(toCartProblem(err, () => t('common.cart.updateItemFailed')))
      }
    })
  }

  const removeItem = async (
    itemId: number,
    storeSlug: string,
    guestToken: string | null = null
  ) => {
    // Captured before the first await: every step below belongs to this identity.
    const generation = identityGeneration.current()
    await runCartCommand({
      generation,
      run: async () => {
        const { baseUrl, token } = cartRequestContext(guestToken)

        const query = CartDestroyQuerySchema.parse({ item_id: itemId })
        const response = await sanctumClient(`${baseUrl}/api/cart/item`, {
          method: 'DELETE',
          query,
          headers: guestTokenHeaders(token)
        })

        CartDestroySuccessSchema.parse(response)
        if (!identityGeneration.isCurrent(generation)) return
        // No response body on 204: a thrown error already short-circuits the run above.
        await refreshCartForStore(token, generation, storeSlug)
      },
      onFailure: (err) => {
        recordCartError(err, () => t('common.cart.removeItemFailed'))
      }
    })
  }

  const clearCart = async (storeSlug: string, guestToken: string | null = null) => {
    // Captured before the first await: every step below belongs to this identity.
    const generation = identityGeneration.current()
    await runCartCommand({
      generation,
      run: async () => {
        const { baseUrl, token } = cartRequestContext(guestToken)

        const query = CartClearQuerySchema.parse({ store_slug: storeSlug })
        const response = await sanctumClient(`${baseUrl}/api/cart`, {
          method: 'DELETE',
          query,
          headers: guestTokenHeaders(token)
        })

        const actualResponse = CartClearSuccessSchema.parse(response)
        if (!identityGeneration.isCurrent(generation)) return
        // Refresh cart data after clearing
        if (actualResponse.success) await refreshCartForStore(token, generation, storeSlug)
      },
      onFailure: (err) => {
        recordCartError(err, () => t('common.cart.clearFailed'))
      }
    })
  }

  const continueOrder = async (storeSlug: string): Promise<void> => {
    cartStore.closeDraftOrdersCart()

    await navigateTo(`/store/${storeSlug}`)

    cartStore.setCurrentStore(storeSlug)
    cartStore.openStoreCart()
  }

  const removeDraft = async (storeSlug: string, guestToken: string | null = null) => {
    await clearCart(storeSlug, guestToken)
  }

  return { addItem, clearCart, continueOrder, fetchCart, removeDraft, removeItem, updateItem }
}
